import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import type { TimeFacts } from '../../utils/recipeTimeFacts';

interface TimeBoxesProps {
  facts: TimeFacts;
  /** Phone column: a touch tighter caption leading. */
  small?: boolean;
}

interface BoxProps {
  testID: string;
  label: string;
  icon: React.ReactNode;
  value: string;
  caption: string | null;
  small?: boolean;
}

const Box: React.FC<BoxProps> = ({ testID, label, icon, value, caption, small }) => (
  <View style={styles.box} testID={testID}>
    <View style={styles.labelRow}>
      {icon}
      <Text style={styles.label}>{label}</Text>
    </View>
    <Text style={styles.value} testID={`${testID}-value`}>{value}</Text>
    {caption != null && (
      <Text style={[styles.caption, small && styles.captionSmall]} testID={`${testID}-caption`}>
        {caption}
      </Text>
    )}
  </View>
);

/**
 * « Préparation » and « Prêt en » side by side (#850): the time the cook's hands
 * are busy and the time until the table. Either may be absent; a lone « Prêt en »
 * takes half the width, never the whole row.
 */
export const TimeBoxes: React.FC<TimeBoxesProps> = ({ facts, small }) => {
  const { t } = useTranslation();
  const hasHandsOn = facts.handsOn != null;
  const hasReady = facts.ready != null;
  if (!hasHandsOn && !hasReady) return null;

  return (
    <View style={styles.row} testID="time-boxes">
      {hasHandsOn && (
        <Box
          testID="time-box-hands-on"
          label={t('recipeTime.handsOn')}
          icon={<Ionicons name="hand-left-outline" size={14} color={Paprika.textSecondary} />}
          value={facts.handsOn!}
          caption={facts.handsOnCaption}
          small={small}
        />
      )}
      {hasReady && (
        <Box
          testID="time-box-ready"
          label={t('recipeTime.ready')}
          icon={<Feather name="clock" size={14} color={Paprika.textSecondary} />}
          value={facts.ready!}
          caption={facts.readyCaption}
          small={small}
        />
      )}
      {!(hasHandsOn && hasReady) && <View style={styles.spacer} />}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    width: '100%',
    gap: 10,
  },
  spacer: {
    flex: 1,
  },
  box: {
    flex: 1,
    minWidth: 0,
    padding: 14,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.surfaceLowest,
    ...PaprikaShadows.card,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    minHeight: 28,
  },
  label: {
    flexShrink: 1,
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.77,
    textTransform: 'uppercase',
    color: Paprika.textSecondary,
  },
  value: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 32,
    lineHeight: 38,
    color: Paprika.textPrimary,
    marginBottom: 4,
  },
  caption: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    lineHeight: 17,
    color: Paprika.textSecondary,
  },
  captionSmall: {
    lineHeight: 16,
  },
});

export default TimeBoxes;
